/**
 * Parwaaz — the deterministic validation engine.
 * Runs identically in the browser (instant feedback while the pilot fills the form) and on the
 * server (authoritative re-check at submit time). Every rule emits a `Finding` — including a
 * `pass` finding when it holds — so the officer sees the full ladder, not just the problems.
 */
import { cardValidity } from './tenant';
import { nameSimilarity } from './names';
import type { Finding, GliderClass, MrzResult, PilotFields, RiskTier, Severity, TenantConfig } from './types';

export interface RuleContext {
  today: string; // YYYY-MM-DD
  tenant: TenantConfig;
  /** Server-only lookups. When undefined, the rules that need them are skipped (not failed). */
  duplicates?: { activeCardForDoc: boolean; onBlocklist: boolean };
}

export const RULE_IDS: string[] = [
  'mrz.format',
  'mrz.checkdigits',
  'doc.expiry',
  'doc.duplicate',
  'doc.blocklist',
  'name.match',
  'visa.covers',
  'arrival.plausible',
  'ins.validity',
  'ins.covers',
  'medical.validity',
  'cert.recognised',
  'cert.holder',
  'cert.expiry',
  'glider.known',
  'glider.class',
  'reserve.present',
  'photo.quality',
  'emergency.distinct',
  'declaration.complete',
  'phone.format',
  'dob.plausible',
];

function find(rule: string, severity: Severity, explanation: string, extra?: Partial<Finding>): Finding {
  return { rule, severity, explanation, ...extra };
}

const INDIAN_DOC_TYPES = new Set(['aadhaar', 'voter_id', 'driving_licence']);

// Glider class rank, low → high. DHV classes map to their EN equivalent for comparison only.
const CLASS_RANK: Record<Exclude<GliderClass, 'unknown'>, number> = {
  'EN-A': 0,
  'DHV-1': 0,
  'EN-B': 1,
  'DHV-1-2': 1,
  'EN-C': 2,
  'DHV-2': 2,
  'EN-D': 3,
  'DHV-2-3': 3,
  CCC: 4,
  'DHV-3': 4,
};

function digitsOnly(s: string): string {
  return s.replace(/\D/g, '');
}

function ageYears(dob: string, today: string): number | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dob);
  const t = /^(\d{4})-(\d{2})-(\d{2})$/.exec(today);
  if (!m || !t) return null;
  const [, by, bm, bd] = m.map(Number) as unknown as [number, number, number, number];
  const [, ty, tm, td] = t.map(Number) as unknown as [number, number, number, number];
  let age = ty - by;
  if (tm < bm || (tm === bm && td < bd)) age--;
  return age;
}

/** Run every rule against `fields`/`mrz` under `ctx`, returning the full findings ladder. */
export function runRules(fields: PilotFields, mrz: MrzResult | null, ctx: RuleContext): Finding[] {
  const { tenant, today } = ctx;
  const { validTo: cardValidTo } = cardValidity(tenant, today);
  const out: Finding[] = [];

  // ── mrz.format / mrz.checkdigits ──────────────────────────────────────────
  if (fields.docType === 'passport') {
    const hasLines = !!(fields.mrzLine1 && fields.mrzLine2);
    if (!hasLines) {
      out.push(find('mrz.format', 'warn', 'MRZ not captured; officer will confirm by eye.'));
    } else if (!mrz || mrz.reason) {
      out.push(
        find('mrz.format', 'fail', `MRZ lines could not be read as a valid passport MRZ${mrz?.reason ? ` (${mrz.reason})` : ''}.`, {
          found: `${fields.mrzLine1} / ${fields.mrzLine2}`,
        }),
      );
    } else {
      out.push(find('mrz.format', 'pass', 'MRZ lines are well-formed.'));
      const allChecksPass = mrz.checks.docNo && mrz.checks.dob && mrz.checks.expiry && mrz.checks.composite;
      if (!allChecksPass) {
        const failedChecks = Object.entries(mrz.checks)
          .filter(([, ok]) => !ok)
          .map(([k]) => k);
        out.push(
          find('mrz.checkdigits', 'fail', 'One or more MRZ check digits do not match — the document number, DOB, expiry or composite line may have been mis-scanned or altered.', {
            found: failedChecks.join(', '),
          }),
        );
      } else {
        out.push(find('mrz.checkdigits', 'pass', 'All MRZ check digits match.'));
      }
    }
  }

  // ── doc.expiry ─────────────────────────────────────────────────────────────
  if (!fields.docExpiry) {
    if (fields.docType === 'passport') {
      out.push(find('doc.expiry', 'fail', 'Passport expiry date is missing.'));
    } else {
      out.push(find('doc.expiry', 'pass', 'No expiry date required for this document type.'));
    }
  } else if (fields.docExpiry < cardValidTo) {
    out.push(
      find('doc.expiry', 'fail', 'The document expires before the Free Flyer Card would, so it will not stay valid for the whole card period.', {
        expected: `>= ${cardValidTo}`,
        found: fields.docExpiry,
      }),
    );
  } else {
    out.push(find('doc.expiry', 'pass', 'Document is valid through the card period.'));
  }

  // ── doc.duplicate / doc.blocklist (server-only) ─────────────────────────────
  if (ctx.duplicates) {
    if (ctx.duplicates.activeCardForDoc) {
      out.push(find('doc.duplicate', 'fail', 'An active Free Flyer Card already exists for this document number.'));
    } else {
      out.push(find('doc.duplicate', 'pass', 'No active card exists for this document number.'));
    }
    if (ctx.duplicates.onBlocklist) {
      out.push(find('doc.blocklist', 'fail', 'This document or pilot is on the office blocklist.'));
    } else {
      out.push(find('doc.blocklist', 'pass', 'Not on the office blocklist.'));
    }
  }

  // ── name.match ───────────────────────────────────────────────────────────
  if (mrz && !mrz.reason && (mrz.surname || mrz.givenNames)) {
    const pilotName = `${fields.givenNames} ${fields.surname}`;
    const mrzName = `${mrz.givenNames} ${mrz.surname}`;
    const sim = nameSimilarity(pilotName, mrzName);
    if (sim >= tenant.rules.nameMatchPass) {
      out.push(find('name.match', 'pass', 'Form name matches the MRZ name.', { found: sim.toFixed(2) }));
    } else if (sim >= tenant.rules.nameMatchWarn) {
      out.push(
        find('name.match', 'warn', 'Form name is close to, but not an exact match for, the MRZ name — officer should confirm by eye.', {
          expected: mrzName,
          found: pilotName,
        }),
      );
    } else {
      out.push(
        find('name.match', 'fail', 'Form name does not match the MRZ name.', { expected: mrzName, found: pilotName }),
      );
    }
  }

  // ── visa.covers / arrival.plausible ─────────────────────────────────────────
  if (fields.isForeign) {
    if (!fields.visaValidFrom || !fields.visaValidTo) {
      out.push(find('visa.covers', 'warn', 'Visa details not provided — officer will check the e-visa.'));
    } else if (fields.visaValidFrom > tenant.season.validFrom || fields.visaValidTo < cardValidTo) {
      out.push(
        find('visa.covers', 'fail', "The visa does not cover the whole season/card period.", {
          expected: `${tenant.season.validFrom}..${cardValidTo}`,
          found: `${fields.visaValidFrom}..${fields.visaValidTo}`,
        }),
      );
    } else {
      out.push(find('visa.covers', 'pass', 'Visa covers the card period.'));
    }

    if (fields.arrivalDate) {
      if (fields.arrivalDate > today) {
        out.push(find('arrival.plausible', 'warn', 'Arrival date is in the future.', { found: fields.arrivalDate }));
      } else if (fields.visaValidFrom && fields.arrivalDate < fields.visaValidFrom) {
        out.push(
          find('arrival.plausible', 'warn', 'Arrival date is before the visa becomes valid.', {
            expected: `>= ${fields.visaValidFrom}`,
            found: fields.arrivalDate,
          }),
        );
      } else {
        out.push(find('arrival.plausible', 'pass', 'Arrival date is plausible.'));
      }
    }
  }

  // ── ins.validity / ins.covers ────────────────────────────────────────────
  if (!fields.insValidTo) {
    out.push(find('ins.validity', 'fail', 'Insurance valid-to date is missing.'));
  } else if (fields.insValidTo < cardValidTo) {
    out.push(
      find('ins.validity', 'fail', 'Insurance expires before the card would.', { expected: `>= ${cardValidTo}`, found: fields.insValidTo }),
    );
  } else {
    out.push(find('ins.validity', 'pass', 'Insurance covers the card period.'));
  }

  {
    const text = fields.insCoverText.toLowerCase();
    const phrase = tenant.rules.insuranceCoverPhrases.find((p) => text.includes(p.toLowerCase()));
    if (phrase) {
      const idx = text.indexOf(phrase.toLowerCase());
      const start = Math.max(0, idx - 40);
      const end = Math.min(fields.insCoverText.length, idx + phrase.length + 40);
      out.push(find('ins.covers', 'pass', 'Insurance wording mentions paragliding/adventure-sport cover.', { evidence: fields.insCoverText.slice(start, end) }));
    } else {
      out.push(find('ins.covers', 'warn', 'Insurance wording does not clearly mention paragliding or adventure-sport cover — officer should confirm with the provider.'));
    }
  }

  // ── medical.validity ─────────────────────────────────────────────────────
  if (!fields.medicalValidTo || fields.medicalValidTo < cardValidTo) {
    out.push(
      find('medical.validity', 'warn', 'Medical certificate is missing or expires before the card would.', {
        expected: `>= ${cardValidTo}`,
        found: fields.medicalValidTo || '(none)',
      }),
    );
  } else {
    out.push(find('medical.validity', 'pass', 'Medical certificate covers the card period.'));
  }

  // ── cert.recognised / cert.holder / cert.expiry ─────────────────────────────
  const bodyOk = tenant.rules.recognisedCertBodies.includes(fields.certBody);
  const levelOk = tenant.rules.certLevels.includes(fields.certLevel);
  if (!bodyOk || !levelOk) {
    out.push(
      find('cert.recognised', 'fail', 'Certifying body or certification level is not on the recognised list.', {
        found: `${fields.certBody} / ${fields.certLevel}`,
      }),
    );
  } else {
    out.push(find('cert.recognised', 'pass', 'Certifying body and level are recognised.'));
  }

  if (fields.certHolderName) {
    const sim = nameSimilarity(fields.certHolderName, `${fields.givenNames} ${fields.surname}`);
    if (sim < tenant.rules.nameMatchWarn) {
      out.push(
        find('cert.holder', 'warn', 'Name on the certificate does not clearly match the pilot name.', {
          expected: `${fields.givenNames} ${fields.surname}`,
          found: fields.certHolderName,
        }),
      );
    } else {
      out.push(find('cert.holder', 'pass', 'Certificate holder name matches the pilot.'));
    }
  }

  if (fields.certExpiry) {
    if (fields.certExpiry < today) {
      out.push(find('cert.expiry', 'fail', 'Certification has expired.', { expected: `>= ${today}`, found: fields.certExpiry }));
    } else {
      out.push(find('cert.expiry', 'pass', 'Certification has not expired.'));
    }
  }

  // ── glider.known / glider.class ─────────────────────────────────────────────
  if (!fields.gliderMake || !fields.gliderModel) {
    out.push(find('glider.known', 'warn', 'Glider make/model not fully stated.'));
  } else {
    out.push(find('glider.known', 'pass', 'Glider make and model stated.'));
  }

  if (fields.gliderClass === 'unknown') {
    out.push(find('glider.class', 'warn', 'Glider class not stated.'));
  } else {
    const maxLabel = tenant.rules.gliderClassMaxByLevel[fields.certLevel];
    const maxRank = maxLabel ? CLASS_RANK[maxLabel as Exclude<GliderClass, 'unknown'>] : undefined;
    const rank = CLASS_RANK[fields.gliderClass];
    if (maxRank !== undefined && rank > maxRank) {
      out.push(
        find('glider.class', 'warn', `Glider class (${fields.gliderClass}) exceeds what is typical for certification level ${fields.certLevel} (${maxLabel}).`, {
          expected: `<= ${maxLabel}`,
          found: fields.gliderClass,
        }),
      );
    } else {
      out.push(find('glider.class', 'pass', 'Glider class is within what is typical for this certification level.'));
    }
  }

  // ── reserve.present ─────────────────────────────────────────────────────
  if (!fields.reserveMake) {
    out.push(find('reserve.present', 'warn', 'Reserve parachute make not stated.'));
  } else {
    out.push(find('reserve.present', 'pass', 'Reserve parachute stated.'));
  }

  // ── emergency.distinct ────────────────────────────────────────────────────
  if (!fields.emgPhone) {
    out.push(find('emergency.distinct', 'fail', 'Emergency contact phone is missing.'));
  } else if (digitsOnly(fields.emgPhone) !== '' && digitsOnly(fields.emgPhone) === digitsOnly(fields.phone)) {
    out.push(find('emergency.distinct', 'fail', "Emergency contact number is the same as the pilot's own number.", { found: fields.emgPhone }));
  } else {
    out.push(find('emergency.distinct', 'pass', 'Emergency contact number is distinct from the pilot number.'));
  }

  // ── declaration.complete ──────────────────────────────────────────────────
  {
    const declOk = fields.declarationAccepted.length === 4 && fields.declarationAccepted.every(Boolean);
    const bondOk = fields.bondAccepted.length === 7 && fields.bondAccepted.every(Boolean);
    const sim = nameSimilarity(fields.typedName, `${fields.givenNames} ${fields.surname}`);
    const signatureOk = sim >= tenant.rules.nameMatchWarn;
    const missing: string[] = [];
    if (!declOk) missing.push('declaration clauses');
    if (!bondOk) missing.push('indemnity bond clauses');
    if (!signatureOk) missing.push('typed signature does not match name');
    if (!fields.consentPurpose) missing.push('data-use consent');
    if (missing.length > 0) {
      out.push(find('declaration.complete', 'fail', 'Declaration is incomplete.', { found: missing.join('; ') }));
    } else {
      out.push(find('declaration.complete', 'pass', 'Declaration, bond and consent are complete.'));
    }
  }

  // ── phone.format ───────────────────────────────────────────────────────
  if (!/^\+[1-9]\d{6,14}$/.test(fields.phone)) {
    out.push(find('phone.format', 'fail', 'Phone number is not in international format (e.g. +919812345678).', { found: fields.phone }));
  } else {
    out.push(find('phone.format', 'pass', 'Phone number is in international format.'));
  }

  // ── dob.plausible ─────────────────────────────────────────────────────────
  {
    const age = ageYears(fields.dob, today);
    if (age === null) {
      out.push(find('dob.plausible', 'fail', 'Date of birth is missing or invalid.'));
    } else if (age < 16 || age > 90) {
      out.push(find('dob.plausible', 'fail', 'Age is outside the plausible range for a paraglider pilot (16-90).', { found: String(age) }));
    } else {
      out.push(find('dob.plausible', 'pass', 'Age is plausible.'));
    }
  }

  return out;
}

/** `photo.quality` is computed in the browser from face/sharpness/brightness analysis of the uploaded photo. */
export function photoFinding(quality: { faces: number; sharpness: number; brightness: number } | null): Finding {
  const rule = 'photo.quality';
  if (!quality) {
    return find(rule, 'fail', 'Photo quality could not be assessed.');
  }
  if (quality.faces === 0) {
    return find(rule, 'fail', 'No face detected in the photo.', { found: 'faces=0' });
  }
  if (quality.faces > 1) {
    return find(rule, 'fail', 'More than one face detected in the photo.', { found: `faces=${quality.faces}` });
  }
  const issues: string[] = [];
  if (quality.sharpness < 0.15) issues.push('looks blurry');
  if (quality.brightness < 0.25 || quality.brightness > 0.85) issues.push('lighting is too dark or too bright');
  if (issues.length > 0) {
    return find(rule, 'warn', `Photo quality issue: ${issues.join(', ')}.`, {
      found: `sharpness=${quality.sharpness.toFixed(2)}, brightness=${quality.brightness.toFixed(2)}`,
    });
  }
  return find(rule, 'pass', 'Photo quality looks good.');
}

/** Overall risk tier: any fail → red; else any warn → amber; else green. */
export function riskTier(findings: Finding[]): RiskTier {
  if (findings.some((f) => f.severity === 'fail')) return 'red';
  if (findings.some((f) => f.severity === 'warn')) return 'amber';
  return 'green';
}

/** Plain-language list of what to fix, drawn from the `fail` findings (deduplicated, in order). */
export function correctionItems(findings: Finding[]): string[] {
  const seen = new Set<string>();
  const items: string[] = [];
  for (const f of findings) {
    if (f.severity !== 'fail') continue;
    if (seen.has(f.explanation)) continue;
    seen.add(f.explanation);
    items.push(f.explanation);
  }
  return items;
}
